import React from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";
import { WHATSAPP_SENDER, WHATSAPP_SENDER_NAME } from "../../config/platformContacts";

/**
 * Platform-wide knowledge base entry. Gives supervisors and new staff a
 * single-page mental model of how Lusuku works end-to-end: what the verticals
 * are, who the actors are, the order lifecycle, the money flow, the audit
 * trail, and where every major feature lives in the sidebar.
 *
 * Content policy: this page ships to production staff. Never include
 * credentials, API keys, internal hostnames, repo names, or source-file
 * paths. Anything developer-only must be gated behind a NODE_ENV check
 * (this page has no dev-only block).
 */
const ACCENT = "#1a2b4a";
const TINT = "#1a2b4a14";
const PILLTEXT = "#1a2b4a";

// Per-vertical pill hues: flat tint background + dark text of the same hue.
const pillHues = {
  restaurant: { bg: "#17385a1a", text: "#17385a" },
  retailer: { bg: "#FF6B1A1a", text: "#a8480e" },
  healthcare: { bg: "#E91E631a", text: "#ad1457" },
  brand: { bg: "#6b1a8a1a", text: "#6b1a8a" },
  threepl: { bg: "#0062021a", text: "#006202" },
  system: { bg: "#5b6b8a1a", text: "#3f4d66" },
};

const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };
const bodyText = { fontSize: 12.5 };
const tableStyle = { fontSize: 12 };
const theadStyle = { background: "#f5f7fb" };
const cardStyle = { border: "1px solid #e5e9f1", borderRadius: 8, boxShadow: "none" };
const insetBox = { borderRadius: 8, padding: 14 };
const insetList = { paddingLeft: 18, marginTop: 6, fontSize: 12.5 };

const Pill = ({ label, hue }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      background: hue ? hue.bg : TINT,
      color: hue ? hue.text : PILLTEXT,
      marginRight: 6,
      marginBottom: 4,
    }}
  >
    {label}
  </span>
);

const SectionIcon = ({ cls }) => (
  <i
    className={cls}
    style={{ fontSize: 15, color: ACCENT, marginRight: 8, verticalAlign: "middle" }}
  />
);

const VERTICAL_PILLS = [
  ["Restaurant", "restaurant"],
  ["Retail", "retailer"],
  ["Wholesale", "retailer"],
  ["Online Retail", "retailer"],
  ["Healthcare", "healthcare"],
  ["Brand Owner", "brand"],
  ["3PL Partner", "threepl"],
  ["3PL Staff", "threepl"],
  ["Rider", "system"],
  ["Customer", "system"],
];

const APPS = [
  {
    app: "Dashboard API + Staff UI",
    tech: "Node 18 / Express + React",
    host: <Badge bg="dark">Heroku</Badge>,
    hostNote: "(API and UI together)",
    users: "Platform admins, supervisors, vendor owners, staff",
  },
  {
    app: "Web storefront",
    tech: "React",
    host: <Badge bg="info">Netlify</Badge>,
    users: "Customers browsing & ordering on the web",
  },
  {
    app: "Consumer mobile app",
    tech: "React Native (Expo)",
    host: "App Store / Play Store",
    users: "Customers on iOS / Android",
  },
  {
    app: "Wallet service",
    tech: "Node 18 / Express (microservice)",
    host: <Badge bg="dark">Heroku</Badge>,
    users: "System-to-system (MTN MOMO disbursements, escrow, refunds)",
  },
  {
    app: "Rider app",
    tech: "React Native (Expo)",
    host: "App Store / Play Store",
    users: "Riders picking up & delivering",
  },
];

const LIFECYCLE = [
  ["PLACED", "Customer submits cart → payment charge attempt"],
  ["CONFIRMED", "Vendor accepts → kitchen / warehouse starts"],
  ["READY", "Order prepared, awaiting pickup"],
  ["ASSIGNED", "Rider / 3PL partner accepts dispatch"],
  ["PICKED_UP", "Rider has the goods"],
  ["DELIVERED", "Customer confirms receipt"],
  ["SETTLED", "Wallet sweeps commission + releases vendor payout"],
];

const LOGGED_ITEMS = [
  "Product CRUD + price / description version history",
  "Stock movements (in / out / adjust)",
  "Order state transitions + reasons",
  "Staff sessions (login, shift end, terminate)",
  "Wallet events (disbursement, refund, escrow release)",
  "Hours changes, outlet toggles, admin interventions",
];

const SIDEBAR_ROWS = [
  { group: "Restaurant / R&W / Healthcare / Brands", purpose: "Per-vertical vendor and catalogue management", paths: ["/admin/brands-management", "/admin/restaurant-management"] },
  { group: "Orders / Delivery / Riders", purpose: "Live operations: dispatch, tracking, rider assignment", paths: ["/admin/orders", "/admin/riders"] },
  { group: "Wallet / Refund / Notifications", purpose: "Money, messages, customer comms — everything ledger-bearing", paths: ["/admin/wallet", "/admin/refunds"] },
  { group: "Audit & Compliance", purpose: "Activity log, stock ledger, staff sessions, reports, data export, feature entitlements", paths: ["/admin/activity-log", "/admin/feature-entitlements"] },
  { group: "Knowledge base", purpose: "This wiki — Platform Overview + vertical-specific deep-dives", paths: ["/admin/wiki/platform-overview"] },
];

const QUICK_ANSWERS = [
  ["Where do I see why an order was cancelled?", "Open the order and read its activity timeline. Every state change records who moved it, when, and the reason; a cancellation branches off but never rewrites the earlier states."],
  ["Who can see a vendor's audit trail?", "Platform admins always see every vendor's trail. Vendor owners always see their own vendor's full trail. Sub-staff only see modules their vendor holds an ACTIVE or TRIAL entitlement for."],
  ["How does a vendor actually get paid?", "Customer funds sit in escrow until delivery is confirmed. On settlement the platform commission is deducted, the vendor's released balance increments, and the payout goes out via MTN mobile money."],
  ["Why does a vendor's balance still say pending?", "The linked order has not reached DELIVERED / SETTLED yet. Escrowed funds only release into the payable balance once the customer's delivery is confirmed and the commission split runs."],
  ["A payout looks wrong — where do I check?", "Start in the Wallet sidebar group. Every shilling is a ledger row, and a nightly reconciliation job matches mobile-money callbacks against those rows; any drift raises an alert for review."],
  ["Where do I manage which vendors get audit features?", "Feature entitlements are managed under Audit & Compliance (super-admins and supervisors only). Setting a module to ACTIVE or TRIAL unlocks it for that vendor's sub-staff."],
  ["What customer details can a vendor see on an order?", "Customer name and phone, the delivery address with map coordinates, the items and quantities, payment method and status, delivery-schedule preference, and any special instructions."],
  ["A vendor says they never got an order alert. Where do I start?", "Their own Profile > Alert settings. The Recent alerts list there shows every alert sent to them with its outcome and reason, and Send test alert reproduces the delivery live on each channel. Skipped almost always means an unconfigured provider or a missing alert contact."],
  ["Which number do WhatsApp alerts come from?", `${WHATSAPP_SENDER}. Vendors should save it, and anything they reply lands in the ${WHATSAPP_SENDER_NAME} inbox.`],
];

const PlatformOverviewWiki = () => {
  return (
    <div>
      {/* Compact header */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>How Lusuku works</div>
        <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 2 }}>
          The actors, the order lifecycle, where the money sits, and what every sidebar item does.
        </div>
      </div>

      {/* Who we serve */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-globe-2" />
            1. Who Lusuku serves
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Lusuku is a multi-vertical commerce + logistics platform. Each vendor belongs to
            exactly one operation mode that determines their catalogue shape, order shape, and
            dashboard surface.
          </p>
          <div>
            {VERTICAL_PILLS.map(([label, hue]) => (
              <Pill key={label} label={label} hue={pillHues[hue]} />
            ))}
          </div>
          <p className="text-muted mt-3 mb-0" style={bodyText}>
            A vendor may own one or many outlets. Every outlet inherits the parent
            vendor&rsquo;s operation mode and keeps its own independent inventory. Staff
            (cashiers, warehouse, managers) are separate user accounts linked to the parent
            vendor via role-based access: owners have full control, managers handle products
            &amp; orders, and agents get limited order-processing access.
          </p>
        </Card.Body>
      </Card>

      {/* Apps in the stack */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-layers-3" />
            2. The apps that make up Lusuku
          </Card.Title>
          <Table responsive hover size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>App</th>
                <th>Tech</th>
                <th>Hosted on</th>
                <th>Who uses it</th>
              </tr>
            </thead>
            <tbody>
              {APPS.map((row) => (
                <tr key={row.app}>
                  <td>
                    <strong>{row.app}</strong>
                  </td>
                  <td>{row.tech}</td>
                  <td>
                    {row.host}
                    {row.hostNote ? <span className="text-muted"> {row.hostNote}</span> : null}
                  </td>
                  <td>{row.users}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="text-muted mt-2 mb-0" style={bodyText}>
            Everything talks to the same central API: real-time order updates over sockets,
            images on a CDN, and customer notifications via push, WhatsApp, and SMS.
          </p>
        </Card.Body>
      </Card>

      {/* Order lifecycle */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            3. The order lifecycle
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Every order &mdash; B2C food, retail pickup, wholesale B2B, brand storefront, or
            in-house &mdash; flows through the same state machine. Staff should always be able
            to tell you <em>which state</em> an order is in and <em>who</em> moved it there.
          </p>
          <Row className="g-2">
            {LIFECYCLE.map(([state, note], i) => (
              <Col md={6} lg={4} key={state}>
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e5e9f1",
                    borderRadius: 8,
                    padding: 12,
                    height: "100%",
                  }}
                >
                  <div style={{ fontSize: 11, color: "#6b7a99" }}>STEP {i + 1}</div>
                  <div
                    style={{ fontWeight: 700, fontSize: 12.5, color: ACCENT, letterSpacing: "0.4px" }}
                  >
                    {state}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {note}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <p className="text-muted mt-3 mb-0" style={bodyText}>
            Cancellations, refunds, and 3PL re-assignments branch off these states but never
            rewrite history &mdash; the prior state stays in the order&rsquo;s event log and the
            vendor&rsquo;s activity log, with the actor and reason attached. Vendors may reject
            an order before confirming it; multi-vendor carts split so each vendor gets its own
            rider assignment.
          </p>
        </Card.Body>
      </Card>

      {/* Money flow */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            4. How money moves
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Every shilling is a ledger row. The Dashboard never writes disbursements directly
            &mdash; the Wallet Service does, via MTN MOMO.
          </p>
          <ol className="mb-0" style={{ paddingLeft: 20, fontSize: 12.5 }}>
            <li>
              <strong>Charge.</strong> Customer pays mobile money (MTN / Airtel), card, or cash
              on delivery at checkout. The Dashboard records the payment attempt.
            </li>
            <li>
              <strong>Escrow.</strong> The Wallet Service holds the funds; the vendor balance
              shows <em>pending</em> until delivery is confirmed.
            </li>
            <li>
              <strong>Commission split.</strong> On <code>SETTLED</code> the platform
              commission is deducted and the vendor&rsquo;s released balance increments.
            </li>
            <li>
              <strong>Disbursement.</strong> Vendor requests a payout (or a bulk payout batch
              runs) &rarr; the Wallet Service issues an MTN MOMO transfer with an idempotency
              key, so a retried payout never pays twice.
            </li>
            <li>
              <strong>Reconciliation.</strong> A nightly job matches MOMO callbacks against
              ledger entries. Any drift raises an alert.
            </li>
            <li>
              <strong>Audit.</strong> Every step is mirrored into the vendor&rsquo;s Activity
              Log on the dashboard via an authenticated service-to-service call, so the trail
              and the ledger always agree.
            </li>
          </ol>
        </Card.Body>
      </Card>

      {/* Audit + entitlements */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-single-copy-04" />
            5. Audit trail &amp; entitlements
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            The audit trail is Lusuku&rsquo;s single most important product promise: every
            vendor-facing state change is recorded, diffed, and queryable. An entitlement gate
            on top decides which vendors&rsquo; sub-staff can use which audit modules.
          </p>
          <Row className="g-2">
            <Col md={6}>
              <div style={{ ...insetBox, background: "#fff8ea", border: "1px solid #f1e0a8" }}>
                <div style={{ fontWeight: 700, color: "#7a5a00", fontSize: 12.5 }}>
                  What gets logged
                </div>
                <ul className="mb-0 text-muted" style={insetList}>
                  {LOGGED_ITEMS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>
            <Col md={6}>
              <div style={{ ...insetBox, background: "#eaf4ff", border: "1px solid #b6d4f0" }}>
                <div style={{ fontWeight: 700, color: "#1a4a7a", fontSize: 12.5 }}>
                  Who sees what
                </div>
                <ul className="mb-0 text-muted" style={insetList}>
                  <li>
                    <strong>Platform admins</strong> bypass all gating &mdash; always see every
                    vendor&rsquo;s trail.
                  </li>
                  <li>
                    <strong>Vendor owners</strong> always see their own vendor&rsquo;s full
                    trail.
                  </li>
                  <li>
                    <strong>Sub-staff</strong> see modules their vendor has an{" "}
                    <code>ACTIVE</code> or <code>TRIAL</code> entitlement for.
                  </li>
                  <li>
                    Manage at <code>/admin/feature-entitlements</code> (super / supervisor
                    only).
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
          <p className="text-muted mt-3 mb-0" style={bodyText}>
            Log entries are append-only: corrections are new entries, never edits, so what a
            supervisor sees in the trail is exactly what happened. Products also pass through a
            verification workflow (PENDING &rarr; VERIFIED / REJECTED) before going live, with
            the approver and date recorded.
          </p>
        </Card.Body>
      </Card>

      {/* Alert settings */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bell-55" />
            6. Alert settings (every vendor type)
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            One shared panel, mounted on every vendor&rsquo;s own Profile screen. Restaurants,
            shops, healthcare providers, brand owners and event organizers all see the same
            thing. It is where a vendor, not Lusuku staff, decides how they are
            reached. A preference left untouched inherits the legacy profile toggle or the
            platform default, and every switch shows the resolved outcome, so what the vendor
            sees is what the dispatcher will actually do.
          </p>
          <Row className="g-2">
            <Col md={6}>
              <div style={{ ...insetBox, background: "#eaf4ff", border: "1px solid #b6d4f0" }}>
                <div style={{ fontWeight: 700, color: "#1a4a7a", fontSize: 12.5 }}>
                  Channels and contacts
                </div>
                <ul className="mb-0 text-muted" style={insetList}>
                  <li>
                    <strong>Channels.</strong> Email, SMS and WhatsApp, each switched on or off
                    on its own. Under every switch the panel states whether that provider is
                    configured on the server, so nobody trusts a dead toggle.
                  </li>
                  <li>
                    <strong>Alert contacts.</strong> An alert email and an alert phone that
                    override the profile contacts. Leave either blank and the profile value is
                    used; the placeholder shows the masked default. Phone numbers must
                    normalise to E.164, e.g. +256 7xx xxx xxx.
                  </li>
                  <li>
                    <strong>Events.</strong> New orders, and changes to my profile.
                  </li>
                </ul>
              </div>
            </Col>
            <Col md={6}>
              <div style={{ ...insetBox, background: "#effcf6", border: "1px solid #b7e4d0" }}>
                <div style={{ fontWeight: 700, color: "#0b6b4f", fontSize: 12.5 }}>
                  Test alert and history
                </div>
                <ul className="mb-0 text-muted" style={insetList}>
                  <li>
                    <strong>Send test alert</strong> fires a real message on every channel that
                    is switched on and lists the per-channel outcome: sent, skipped (with the
                    reason, usually an unconfigured provider or a missing contact), or failed.
                    It is rate limited.
                  </li>
                  <li>
                    <strong>Recent alerts</strong> lists the last alerts sent to that vendor,
                    with the event, channel, outcome and when. It is the first place to look
                    when a vendor says they were never told.
                  </li>
                  <li>
                    <strong>WhatsApp comes from {WHATSAPP_SENDER}.</strong> Tell vendors
                    to save that number, and expect their replies to land in the{" "}
                    {WHATSAPP_SENDER_NAME} inbox.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
          <p className="text-muted mt-3 mb-0" style={bodyText}>
            Staff-side toggles on the admin vendor screens are now only a fallback: they apply
            while the vendor has not set that preference for themselves. Point a vendor at
            Profile &rsaquo; Alert settings rather than changing it for them.
          </p>
        </Card.Body>
      </Card>

      {/* Sidebar guide */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" />
            7. Sidebar cheat-sheet
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            What each top-level sidebar group does &mdash; from a supervisor&rsquo;s
            perspective.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Group</th>
                <th>Purpose</th>
                <th>Key paths</th>
              </tr>
            </thead>
            <tbody>
              {SIDEBAR_ROWS.map((row) => (
                <tr key={row.group}>
                  <td>
                    <strong>{row.group}</strong>
                  </td>
                  <td>{row.purpose}</td>
                  <td className="text-muted">
                    {row.paths.map((p, i) => (
                      <span key={p}>
                        {i > 0 ? ", " : ""}
                        <code>{p}</code>
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Quick answers */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            8. Quick answers
          </Card.Title>
          {QUICK_ANSWERS.map(([q, a], i) => (
            <div
              key={q}
              style={{ padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid #eef1f6" }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                {a}
              </div>
            </div>
          ))}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PlatformOverviewWiki;
